import React, { useState, useEffect, useRef } from 'react';
import DivisionBillingSettings from '../components/DivisionBillingSettings';
import { businessesAPI } from '../services/api';
import { invitesAPI } from '../features/businesses/services/invitesApi';
import BankFeedsAccess from '../features/businesses/components/BankFeedsAccess'; import DatingMethodSetting from '../features/reconciliation/DatingMethodSetting'; import ClientPortalSettings from '../features/client-portal/ClientPortalSettings';
import adminApi from '../services/adminApi';
import ErrorBoundary from '../components/ErrorBoundary';
import BusinessLogoManager from '../components/BusinessLogoManager';
import formationDateIsCurrent from '../utils/formationDate';
import { formatApiError } from '../utils/errorMessage';
import { assetUrl } from '../utils/assetUrl';
import { parseCalendarDay } from '../utils/localDate';
import { formationLockState, graceMessage } from '../utils/formationLock';
import { SetupChecklist, SetupProgressLabel } from '../features/businessSetup/SetupChecklist';

const INVITE_ROLES = [
  { value: 'admin', label: 'Admin', sees: 'Every screen except deleting the location. Can invite.' },
  { value: 'manager', label: 'Manager', sees: 'Books, customers, bills, properties, and reports. Can see the team. Cannot change people, payroll, or billing.' },
  { value: 'accountant', label: 'Accountant', sees: 'The manager screens plus payroll and staff, without Team.' },
  { value: 'assistant', label: 'Assistant', sees: 'Transactions, budgets, customers, and bills. Can add and edit. Cannot delete a transaction.' },
  { value: 'data_entry', label: 'Data entry', sees: 'Dashboard, transactions, and categories. Can add a transaction. Cannot edit or delete one.' },
  { value: 'viewer', label: 'Viewer', sees: 'Dashboard, transactions, accounts, and reports. Cannot save.' }
];

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const LEGAL_STRUCTURES = [
  'LLC', 'Corporation', 'S-Corp', 'C-Corp', 'Partnership',
  'Sole Proprietorship', 'Non-Profit', 'LLP', 'Personal', 'Personal Use', 'Other'
];

const FORMED_STRUCTURES = ['LLC', 'Corporation', 'S-Corp', 'C-Corp', 'Partnership', 'Non-Profit', 'LLP'];

const FORMATION_DOCUMENTS = [
  { kind: 'formation_paper', label: 'Formation paper' },
  { kind: 'ein_letter', label: 'IRS EIN letter (CP 575 or 147C)', hint: 'The IRS letter that shows your EIN: CP 575 when it was assigned, or 147C if you asked the IRS to confirm it.' },
  { kind: 'owner_id_photo', label: 'Owner ID photo' }
];

const REGISTRATION_TYPES = [
  'Primary Registration',
  'Foreign Entity',
  'DBA/Trade Name',
  'Assumed Name',
  'Fictitious Name'
];

const RENEWAL_FREQUENCIES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'semi-annually', label: 'Semi-Annually' },
  { value: 'annually', label: 'Annually' },
  { value: 'biennial', label: 'Biennial (Every 2 Years)' }
];

const MAX_LOGO_SIZE_MB = 5;
const BUSINESSES_UPDATED_EVENT = 'businesses-updated';

function readFormationDateEdits() {
  try {
    const raw = sessionStorage.getItem('formationDateEdits');
    const parsed = raw ? JSON.parse(raw) : {};
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed;
  } catch (error) {
    return {};
  }
}

function writeFormationDateEdits(edits) {
  try {
    sessionStorage.setItem('formationDateEdits', JSON.stringify(edits));
  } catch (error) {
    // The typed date stays on this page if the browser refuses storage.
  }
}

const notifyBusinessesUpdated = (detail = {}) => {
  try {
    window.dispatchEvent(new CustomEvent(BUSINESSES_UPDATED_EVENT, { detail }));
  } catch {
    // ignore (non-browser / older env)
  }
};

function Business({ selectedBusiness: globalSelectedBusiness, setSelectedBusiness: setGlobalSelectedBusiness }) {
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(globalSelectedBusiness || null);
  const [stateRegistrations, setStateRegistrations] = useState([]);
  const [registeredAgents, setRegisteredAgents] = useState([]);
  const [complianceCalendar, setComplianceCalendar] = useState([]);
  const [databaseToday, setDatabaseToday] = useState('');
  const [dateEdits, setDateEdits] = useState(readFormationDateEdits);
  useEffect(() => {
    writeFormationDateEdits(dateEdits);
  }, [dateEdits]);
  const [formationDocuments, setFormationDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details'); // details, registrations, agents, team, setup, calendar
  const [setupRefresh, setSetupRefresh] = useState(0);
  useEffect(() => {
    // The dashboard card links to /business?tab=setup.
    if (new URLSearchParams(window.location.search).get('tab') === 'setup') setActiveTab('setup');
  }, []);

  // Modals
  const [showBusinessModal, setShowBusinessModal] = useState(false);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [showAgentModal, setShowAgentModal] = useState(false);

  // Team access
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamInvites, setTeamInvites] = useState([]);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    member_role: 'assistant',
    state_registration_ids: []
  });
  const [teamLoading, setTeamLoading] = useState(false);

  // Form data
  const [businessForm, setBusinessForm] = useState({
    name: '', legal_name: '', legal_structure: 'LLC', tax_id: '',
    industry: '', description: '', address_line1: '', address_line2: '',
    city: '', state: '', zip_code: '', country: 'USA', phone: '', email: '',
    website: '', established_date: '', fiscal_year_end_month: 12,
    fiscal_year_end_day: 31, notes: '', parent_business_id: ''
  });
  const [businessLogoFile, setBusinessLogoFile] = useState(null);
  const [businessLogoPreview, setBusinessLogoPreview] = useState(null);
  const businessLogoInputRef = useRef(null);

  const [registrationForm, setRegistrationForm] = useState({
    state: '', registration_type: 'Primary Registration', dba_name: '',
    registration_number: '', registration_date: '', status: 'active',
    expiration_date: '', annual_report_due_date: '', filing_fee: '', notes: '',
    is_subsidiary: false, subsidiary_name: '', industry: ''
  });

  const [agentForm, setAgentForm] = useState({
    state: '', agent_type: 'registered_agent', agent_name: '', agent_company: '',
    agent_address_line1: '', agent_address_line2: '', agent_city: '',
    agent_state: '', agent_zip: '', agent_phone: '', agent_email: '',
    hire_date: '', renewal_frequency: 'annually', renewal_frequency_value: 1,
    last_renewal_date: '', cost_per_renewal: '', auto_renew: false,
    status: 'active', notes: ''
  });

  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => {
    loadBusinesses();
  }, []);

  const selectedStructure = businesses.find((row) => Number(row.id) === Number(selectedBusiness))?.legal_structure;
  const storeFormationDocuments = FORMED_STRUCTURES.includes(selectedStructure);

  useEffect(() => {
    if (selectedBusiness) {
      loadBusinessData(selectedBusiness, storeFormationDocuments);
      if (activeTab === 'team') {
        loadTeamAccess(selectedBusiness);
      }
    }
  }, [selectedBusiness, activeTab, storeFormationDocuments]);

  useEffect(() => {
    if (globalSelectedBusiness && globalSelectedBusiness !== selectedBusiness) {
      setSelectedBusiness(globalSelectedBusiness);
    }
  }, [globalSelectedBusiness, selectedBusiness]);

  const updateSelectedBusiness = (businessId) => {
    const id = Number(businessId) || null;
    setSelectedBusiness(id);
    if (setGlobalSelectedBusiness) {
      setGlobalSelectedBusiness(id);
    }
  };

  const loadBusinesses = async () => {
    try {
      setLoading(true);
      const response = await businessesAPI.getAll();
      setBusinesses(response.data);
      if (response.data.length > 0) {
        if (!globalSelectedBusiness) {
          updateSelectedBusiness(response.data[0].id);
        }
      } else {
        updateSelectedBusiness(null);
      }
    } catch (error) {
      console.error('Failed to load businesses:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert('Failed to load businesses');
    } finally {
      setLoading(false);
    }
  };

  const loadBusinessData = async (businessId, storeFormationDocuments) => {
    if (!storeFormationDocuments) {
      setFormationDocuments([]);
    } else {
      try {
        const documentsResponse = await businessesAPI.listFormationDocuments(businessId);
        setFormationDocuments(documentsResponse.data || []);
      } catch (documentError) {
        setFormationDocuments([]);
      }
    }
    try {
      const calendarResponse = await businessesAPI.getComplianceCalendar(businessId);
      setComplianceCalendar(calendarResponse.data?.items || []);
      setDatabaseToday(calendarResponse.data?.today || '');
    } catch (error) {
      console.error('Failed to load business data:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
    }
    try {
      const [regsResponse, agentsResponse] = await Promise.all([
        businessesAPI.getStateRegistrations(businessId),
        businessesAPI.getRegisteredAgents(businessId)
      ]);
      setStateRegistrations(regsResponse.data);
      setRegisteredAgents(agentsResponse.data);
    } catch (error) {
      setStateRegistrations([]);
      setRegisteredAgents([]);
    }
  };

  const openFormationDocument = (kind) => {
    const opened = window.open('about:blank', '_blank');
    businessesAPI.openFormationDocument(currentBusiness.id, kind).then((response) => {
      const type = response.headers?.['content-type'] || 'application/octet-stream';
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type });
      const url = URL.createObjectURL(blob);
      if (opened) opened.location.href = url;
    }).catch(() => {
      if (opened) opened.close();
    });
  };

  const loadTeamAccess = async (businessId) => {
    try {
      setTeamLoading(true);
      const [membersRes, invitesRes] = await Promise.all([
        invitesAPI.listMembers(businessId),
        invitesAPI.list(businessId)
      ]);
      setTeamMembers(membersRes.data || []);
      setTeamInvites((invitesRes.data || []).filter((i) => i.status === 'pending'));
    } catch (error) {
      console.error('Failed to load team access:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      setTeamMembers([]);
      setTeamInvites([]);
    } finally {
      setTeamLoading(false);
    }
  };

  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    if (!selectedBusiness) return;
    try {
      await invitesAPI.create(selectedBusiness, {
        email: inviteForm.email.trim(),
        member_role: inviteForm.member_role,
        state_registration_ids: inviteForm.state_registration_ids
      });
      setInviteForm({ email: '', member_role: 'assistant', state_registration_ids: [] });
      await loadTeamAccess(selectedBusiness);
      alert('Invite created. The user can accept it from Settings after signing in with that email.');
    } catch (error) {
      alert(formatApiError(error, 'Failed to create invite'));
    }
  };

  const toggleInviteDivision = (regId) => {
    const id = Number(regId);
    setInviteForm((prev) => {
      const exists = prev.state_registration_ids.includes(id);
      return {
        ...prev,
        state_registration_ids: exists
          ? prev.state_registration_ids.filter((x) => x !== id)
          : [...prev.state_registration_ids, id]
      };
    });
  };

  const handleRevokeInvite = async (token) => {
    if (!window.confirm('Revoke this invite?')) return;
    try {
      await invitesAPI.revoke(token);
      await loadTeamAccess(selectedBusiness);
    } catch (error) {
      alert(formatApiError(error, 'Failed to revoke invite'));
    }
  };

  const handleUpdateMemberDivisions = async (userId, currentIds, regId) => {
    const id = Number(regId);
    const next = currentIds.includes(id)
      ? currentIds.filter((x) => x !== id)
      : [...currentIds, id];
    try {
      await invitesAPI.updateMemberDivisions(selectedBusiness, userId, next);
      await loadTeamAccess(selectedBusiness);
    } catch (error) {
      alert(formatApiError(error, 'Failed to update division access'));
    }
  };

  // Business CRUD
  const handleBusinessLogoSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_LOGO_SIZE_MB * 1024 * 1024) {
      alert(`Logo must be smaller than ${MAX_LOGO_SIZE_MB}MB.`);
      event.target.value = '';
      return;
    }
    setBusinessLogoFile(file);
    setBusinessLogoPreview(URL.createObjectURL(file));
  };

  const clearBusinessLogoSelection = () => {
    setBusinessLogoFile(null);
    setBusinessLogoPreview(null);
    if (businessLogoInputRef.current) {
      businessLogoInputRef.current.value = '';
    }
  };

  const handleBusinessSubmit = async (e) => {
    e.preventDefault();
    try {
      // Clean the data - remove empty strings for date fields
      const cleanedData = { ...businessForm };
      if (cleanedData.established_date === '') {
        delete cleanedData.established_date;
      }
      // Empty parent = top-level parent business; keep explicit null so update can clear it
      if (cleanedData.parent_business_id === '' || cleanedData.parent_business_id === null) {
        cleanedData.parent_business_id = null;
      } else {
        cleanedData.parent_business_id = Number(cleanedData.parent_business_id);
      }
      
      let savedBusinessId = editingItem || null;

      if (editingItem) {
        await businessesAPI.update(editingItem, cleanedData);
        savedBusinessId = editingItem;
      } else {
        const response = await businessesAPI.create(cleanedData);
        savedBusinessId = response?.data?.id || null;
      }

      if (businessLogoFile && savedBusinessId) {
        await businessesAPI.uploadLogo(savedBusinessId, businessLogoFile);
      }
      setShowBusinessModal(false);
      setEditingItem(null);
      resetBusinessForm();
      clearBusinessLogoSelection();
      await loadBusinesses();
      if (savedBusinessId) {
        updateSelectedBusiness(savedBusinessId);
      }
      notifyBusinessesUpdated({ businessId: savedBusinessId });
    } catch (error) {
      console.error('Failed to save business:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert(formatApiError(error, 'Failed to save business'));
    }
  };

  const handleEditBusiness = (business) => {
    setBusinessForm({
      ...business,
      established_date: business.established_date?.split?.('T')?.[0] || business.established_date || '',
      parent_business_id: business.parent_business_id || ''
    });
    setEditingItem(business.id);
    setShowBusinessModal(true);
    setBusinessLogoPreview(assetUrl(business.logo_url));
    setBusinessLogoFile(null);
  };

  const openCreateParentModal = () => {
    setEditingItem(null);
    setBusinessForm({
      name: '', legal_name: '', legal_structure: 'LLC', tax_id: '',
      industry: '', description: '', address_line1: '', address_line2: '',
      city: '', state: '', zip_code: '', country: 'USA', phone: '', email: '',
      website: '', established_date: '', fiscal_year_end_month: 12,
      fiscal_year_end_day: 31, notes: '', parent_business_id: ''
    });
    setBusinessLogoFile(null);
    setBusinessLogoPreview(null);
    setShowBusinessModal(true);
  };

  const openCreateChildModal = (parentId = selectedBusiness, presets = {}) => {
    setEditingItem(null);
    setBusinessForm({
      name: presets.name || '',
      legal_name: presets.legal_name || presets.name || '',
      legal_structure: presets.legal_structure || 'LLC',
      tax_id: '',
      industry: presets.industry || '',
      description: presets.description || '',
      address_line1: '', address_line2: '',
      city: '', state: '', zip_code: '', country: 'USA', phone: '', email: '',
      website: '', established_date: '', fiscal_year_end_month: 12,
      fiscal_year_end_day: 31, notes: '',
      parent_business_id: parentId ? String(parentId) : ''
    });
    setBusinessLogoFile(null);
    setBusinessLogoPreview(null);
    setShowBusinessModal(true);
  };

  const getBusinessOptionLabel = (business) => {
    const label = business.name || business.legal_name || `Business ${business.id}`;
    if (business.parent_business_id) {
      return `↳ ${label}`;
    }
    return label;
  };

  const getParentCandidates = (excludeId = null) => {
    // Parents must be businesses that are not the one being edited and not its descendants
    const excluded = new Set();
    if (excludeId) {
      excluded.add(Number(excludeId));
      const collectDescendants = (id) => {
        businesses
          .filter((b) => Number(b.parent_business_id) === Number(id))
          .forEach((child) => {
            excluded.add(Number(child.id));
            collectDescendants(child.id);
          });
      };
      collectDescendants(excludeId);
    }
    return businesses.filter((b) => !excluded.has(Number(b.id)));
  };

  const handleDeleteBusiness = async (id) => {
    const children = businesses.filter((b) => Number(b.parent_business_id) === Number(id));
    const childNote = children.length > 0
      ? `\n\nThis parent has ${children.length} child business(es). Those children will remain but will no longer be linked to this parent.`
      : '';
    if (!window.confirm(
      `Delete this business? This will also delete all related registrations and agents.${childNote}`
    )) return;
    try {
      await businessesAPI.delete(id);
      await loadBusinesses();
      if (Number(selectedBusiness) === Number(id)) {
        updateSelectedBusiness(businesses.find((b) => Number(b.id) !== Number(id))?.id || null);
      }
      notifyBusinessesUpdated({ deletedBusinessId: id });
    } catch (error) {
      console.error('Failed to delete business:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert('Failed to delete business');
    }
  };

  // State Registration CRUD
  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();
    if (!selectedBusiness) return;
    try {
      // Empty date/number strings become null — Postgres rejects "" for DATE/NUMERIC
      const cleanedRegistration = {
        ...registrationForm,
        dba_name: registrationForm.dba_name || null,
        registration_number: registrationForm.registration_number || null,
        registration_date: registrationForm.registration_date || null,
        expiration_date: registrationForm.expiration_date || null,
        annual_report_due_date: registrationForm.annual_report_due_date || null,
        filing_fee: registrationForm.filing_fee === '' || registrationForm.filing_fee === null
          ? null
          : registrationForm.filing_fee,
        notes: registrationForm.notes || null,
        subsidiary_name: registrationForm.is_subsidiary
          ? (registrationForm.subsidiary_name || null)
          : null,
        industry: registrationForm.is_subsidiary
          ? (registrationForm.industry || null)
          : null
      };

      const wasCreatingDivision = !editingItem && cleanedRegistration.is_subsidiary;
      const divisionName = cleanedRegistration.subsidiary_name || cleanedRegistration.dba_name || '';
      const divisionIndustry = cleanedRegistration.industry || '';

      if (editingItem) {
        await businessesAPI.updateStateRegistration(selectedBusiness, editingItem, cleanedRegistration);
      } else {
        await businessesAPI.createStateRegistration(selectedBusiness, cleanedRegistration);
      }
      setShowRegistrationModal(false);
      setEditingItem(null);
      resetRegistrationForm();
      await loadBusinessData(selectedBusiness);

      // Divisions stay under the parent EIN and do not appear in the left selector.
      // Offer to create a real child business when the user needs separate tracking.
      if (wasCreatingDivision) {
        const createChild = window.confirm(
          `"${divisionName || 'This division'}" was saved as a same-EIN division/DBA under the parent.\n\n` +
          'It will NOT appear in the left business selector.\n\n' +
          'Create a Child Business instead so it shows in the sidebar with its own accounts and data tracking?'
        );
        if (createChild) {
          openCreateChildModal(selectedBusiness, {
            name: divisionName,
            industry: divisionIndustry,
            description: divisionName
              ? `Operating division spun out as a child business from registration: ${divisionName}`
              : ''
          });
        }
      }
    } catch (error) {
      console.error('Failed to save registration:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert(formatApiError(error, 'Failed to save registration'));
    }
  };

  const handleEditRegistration = (registration) => {
    setRegistrationForm({
      ...registration,
      registration_date: registration.registration_date?.split('T')[0] || '',
      expiration_date: registration.expiration_date?.split('T')[0] || '',
      annual_report_due_date: registration.annual_report_due_date?.split('T')[0] || ''
    });
    setEditingItem(registration.id);
    setShowRegistrationModal(true);
  };

  const handleDeleteRegistration = async (id) => {
    if (!window.confirm('Delete this state registration?')) return;
    try {
      await businessesAPI.deleteStateRegistration(selectedBusiness, id);
      await loadBusinessData(selectedBusiness);
    } catch (error) {
      console.error('Failed to delete registration:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert('Failed to delete registration');
    }
  };

  // Registered Agent CRUD
  const handleAgentSubmit = async (e) => {
    e.preventDefault();
    if (!selectedBusiness) return;
    try {
      if (editingItem) {
        await businessesAPI.updateRegisteredAgent(selectedBusiness, editingItem, agentForm);
      } else {
        await businessesAPI.createRegisteredAgent(selectedBusiness, agentForm);
      }
      setShowAgentModal(false);
      setEditingItem(null);
      resetAgentForm();
      await loadBusinessData(selectedBusiness);
    } catch (error) {
      console.error('Failed to save agent:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert(formatApiError(error, 'Failed to save registered agent'));
    }
  };

  const handleEditAgent = (agent) => {
    setAgentForm({
      ...agent,
      hire_date: agent.hire_date?.split('T')[0] || '',
      last_renewal_date: agent.last_renewal_date?.split('T')[0] || '',
      next_renewal_date: agent.next_renewal_date?.split('T')[0] || ''
    });
    setEditingItem(agent.id);
    setShowAgentModal(true);
  };

  const handleDeleteAgent = async (id) => {
    if (!window.confirm('Delete this registered agent?')) return;
    try {
      await businessesAPI.deleteRegisteredAgent(selectedBusiness, id);
      await loadBusinessData(selectedBusiness);
    } catch (error) {
      console.error('Failed to delete agent:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert('Failed to delete registered agent');
    }
  };

  // Reset forms
  const resetBusinessForm = () => {
    setBusinessForm({
      name: '', legal_name: '', legal_structure: 'LLC', tax_id: '',
      industry: '', description: '', address_line1: '', address_line2: '',
      city: '', state: '', zip_code: '', country: 'USA', phone: '', email: '',
      website: '', established_date: '', fiscal_year_end_month: 12,
      fiscal_year_end_day: 31, notes: '', parent_business_id: ''
    });
    setBusinessLogoFile(null);
    setBusinessLogoPreview(null);
  };

  const resetRegistrationForm = () => {
    setRegistrationForm({
      state: '', registration_type: 'Primary Registration', dba_name: '',
      registration_number: '', registration_date: '', status: 'active',
      expiration_date: '', annual_report_due_date: '', filing_fee: '', notes: '',
      is_subsidiary: false, subsidiary_name: '', industry: ''
    });
  };

  const resetAgentForm = () => {
    setAgentForm({
      state: '', agent_type: 'registered_agent', agent_name: '', agent_company: '',
      agent_address_line1: '', agent_address_line2: '', agent_city: '',
      agent_state: '', agent_zip: '', agent_phone: '', agent_email: '',
      hire_date: '', renewal_frequency: 'annually', renewal_frequency_value: 1,
      last_renewal_date: '', cost_per_renewal: '', auto_renew: false,
      status: 'active', notes: ''
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      expired: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || colors.active}`}>
        {status}
      </span>
    );
  };

  const getRenewalAlert = (nextRenewalDate) => {
    if (!nextRenewalDate) return null;
    const daysUntil = Math.ceil((parseCalendarDay(nextRenewalDate) - new Date()) / (1000 * 60 * 60 * 24));
    
    if (daysUntil < 0) {
      return <span className="text-red-600 font-medium">⚠️ OVERDUE</span>;
    } else if (daysUntil <= 30) {
      return <span className="text-orange-600 font-medium">⚠️ Due Soon ({daysUntil} days)</span>;
    } else if (daysUntil <= 90) {
      return <span className="text-yellow-600">📅 {daysUntil} days</span>;
    }
    return <span className="text-gray-600">{daysUntil} days</span>;
  };

  const currentBusiness = businesses.find(b => b.id === selectedBusiness);
  const formationDocumentsReady = FORMATION_DOCUMENTS.every((document) =>
    formationDocuments.some((row) => row.document_kind === document.kind)
  );
  const currentFormationLock = formationLockState(
    currentBusiness,
    Date.now(),
    formationDocumentsReady ? 3 : undefined
  );
  // Open for non-formed locations, once all three documents are stored, and
  // during the grace period after a formed location is created.
  const filingScreensOpen = !currentFormationLock.locked;
  const editedBusiness = editingItem ? businesses.find((row) => Number(row.id) === Number(editingItem)) : null;
  const editedDocumentsReady = editedBusiness && (
    Number(editedBusiness.id) === Number(currentBusiness?.id)
      ? formationDocumentsReady
      : Number(editedBusiness.formation_document_count || 0) >= 3
  );
  const editFilingOpen = !editedBusiness
    || !FORMED_STRUCTURES.includes(editedBusiness.legal_structure)
    || editedDocumentsReady
    || formationLockState(editedBusiness).inGrace;
  const creatingFormedLocation = !editingItem && FORMED_STRUCTURES.includes(businessForm.legal_structure);
  const teamRole = currentBusiness?.caller_role;
  const canSeeTeam = ['owner', 'admin', 'manager'].includes(teamRole);
  const canInviteTeam = teamRole === 'owner' || teamRole === 'admin';
  const canManagePeople = teamRole === 'owner';
  const childBusinesses = businesses.filter(
    (b) => Number(b.parent_business_id) === Number(selectedBusiness)
  );
  const parentBusiness = currentBusiness?.parent_business_id
    ? businesses.find((b) => Number(b.id) === Number(currentBusiness.parent_business_id))
    : null;
  const isChildForm = !!businessForm.parent_business_id;
  const parentCandidates = getParentCandidates(editingItem);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl">Loading businesses...</div>
      </div>
    );
  }

  return (
    <ErrorBoundary section="Business Management">
    <div className="p-4 lg:p-6 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4 lg:mb-6">
        <h1 className="text-xl lg:text-3xl font-bold">Business Management</h1>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={openCreateParentModal}
            className="bg-blue-600 text-white px-3 lg:px-4 py-2 rounded-lg hover:bg-blue-700 text-sm lg:text-base"
          >
            + Add Parent Business
          </button>
          {businesses.length > 0 && (
            <button
              onClick={() => openCreateChildModal(selectedBusiness)}
              className="bg-indigo-600 text-white px-3 lg:px-4 py-2 rounded-lg hover:bg-indigo-700 text-sm lg:text-base"
            >
              + Add Child Business
            </button>
          )}
        </div>
      </div>

      {/* Business Selector */}
      {businesses.length > 0 && (
        <div className="mb-4 lg:mb-6 bg-white rounded-lg shadow p-3 lg:p-4">
          <label className="block text-xs lg:text-sm font-medium mb-2">Select Business:</label>
          <select
            value={selectedBusiness || ''}
            onChange={(e) => updateSelectedBusiness(e.target.value)}
            className="w-full p-2 border rounded-lg text-sm lg:text-base"
          >
            {businesses.map(business => (
              <option key={business.id} value={business.id}>
                {getBusinessOptionLabel(business)}
              </option>
            ))}
          </select>
          {currentBusiness?.legal_structure !== 'Personal' && (
          <p className="text-xs text-gray-500 mt-2">
            Indented names (↳) are child businesses with their own data tracking and appear in the left sidebar.
            State registration divisions/DBAs stay under the parent EIN and do not appear in that list — use
            {' '}<span className="font-medium">+ Add Child Business</span> for a selectable entity.
          </p>
          )}
        </div>
      )}

      {businesses.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-600 mb-4">No businesses found. Create your first parent business to get started!</p>
          <button
            onClick={openCreateParentModal}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Create First Business
          </button>
        </div>
      ) : currentBusiness ? (
        <>
          {/* Tabs */}
          <div className="bg-white rounded-lg shadow mb-4 lg:mb-6">
            <div className="border-b border-gray-200 overflow-x-auto">
              <nav className="flex -mb-px">
                {teamRole !== 'manager' && (
                <>
                <button
                  onClick={() => setActiveTab('details')}
                  className={`px-4 lg:px-6 py-3 border-b-2 font-medium text-xs lg:text-sm whitespace-nowrap ${
                    activeTab === 'details'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="hidden sm:inline">Business Details</span>
                  <span className="sm:hidden">Details</span>
                </button>
                {currentBusiness.legal_structure !== 'Personal' && filingScreensOpen && (
                <>
                <button
                  onClick={() => setActiveTab('registrations')}
                  className={`px-4 lg:px-6 py-3 border-b-2 font-medium text-xs lg:text-sm whitespace-nowrap ${
                    activeTab === 'registrations'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="hidden sm:inline">State Registrations ({stateRegistrations.length})</span>
                  <span className="sm:hidden">States ({stateRegistrations.length})</span>
                </button>
                <button
                  onClick={() => setActiveTab('agents')}
                  className={`px-4 lg:px-6 py-3 border-b-2 font-medium text-xs lg:text-sm whitespace-nowrap ${
                    activeTab === 'agents'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="hidden sm:inline">Registered Agents ({registeredAgents.length})</span>
                  <span className="sm:hidden">Agents ({registeredAgents.length})</span>
                </button>
                </>
                )}
                </>
                )}
                {canSeeTeam && (
                <button
                  onClick={() => setActiveTab('team')}
                  className={`px-4 lg:px-6 py-3 border-b-2 font-medium text-xs lg:text-sm whitespace-nowrap ${
                    activeTab === 'team'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="hidden sm:inline">Team Access</span>
                  <span className="sm:hidden">Team</span>
                </button>
                )}
                {teamRole !== 'manager' && !['Personal', 'Personal Use'].includes(currentBusiness?.legal_structure) && (
                <button
                  onClick={() => setActiveTab('setup')}
                  data-setup-tab="true"
                  className={`px-4 lg:px-6 py-3 border-b-2 font-medium text-xs lg:text-sm whitespace-nowrap ${
                    activeTab === 'setup'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Setup checklist
                  <SetupProgressLabel businessId={currentBusiness?.id} refreshKey={setupRefresh} />
                </button>
                )}
                {teamRole !== 'manager' && (
                <button
                  onClick={() => setActiveTab('calendar')}
                  className={`px-4 lg:px-6 py-3 border-b-2 font-medium text-xs lg:text-sm whitespace-nowrap ${
                    activeTab === 'calendar'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Compliance calendar
                </button>
                )}
              </nav>
            </div>

            {currentFormationLock.formed && !currentFormationLock.docsReady && (
              <div data-formation-grace={currentFormationLock.inGrace ? 'true' : 'false'} className="mx-4 lg:mx-6 mt-4 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                {currentFormationLock.inGrace
                  ? graceMessage(currentFormationLock.daysLeft)
                  : 'These screens stay closed until the formation paper, EIN letter, and owner ID photo are stored.'}{' '}
                <button type="button" onClick={() => setActiveTab('calendar')} className="font-medium underline">
                  Upload them in the Compliance calendar
                </button>
              </div>
            )}
            {activeTab === 'setup' && teamRole !== 'manager' && (
              <div className="p-4 lg:p-6">
                <h2 className="text-lg lg:text-2xl font-bold mb-4">Business setup checklist</h2>
                <SetupChecklist
                  key={currentBusiness.id}
                  businessId={currentBusiness.id}
                  onOpenDocuments={() => setActiveTab('calendar')}
                  onLoaded={() => setSetupRefresh((n) => n + 1)}
                />
              </div>
            )}
            {activeTab === 'calendar' && (
              <div className="p-4 lg:p-6">
                <h2 className="text-lg lg:text-2xl font-bold mb-4">Compliance calendar</h2>
                {FORMED_STRUCTURES.includes(currentBusiness?.legal_structure) && (
                  <div className="mb-6 space-y-3">
                    <h3 className="font-semibold">Formation documents</h3>
                    {FORMATION_DOCUMENTS.map((document) => {
                      const stored = formationDocuments.find((row) => row.document_kind === document.kind);
                      const storedDate = stored?.document_date || '';
                      const fieldDate = formationDateIsCurrent.formationFieldDate(storedDate, databaseToday);
                      const editKey = `${currentBusiness.id}:${document.kind}`;
                      const shownDate = Object.prototype.hasOwnProperty.call(dateEdits, editKey)
                        ? dateEdits[editKey]
                        : fieldDate;
                      return (
                        <form
                          key={document.kind}
                          className="flex flex-col sm:flex-row sm:items-end gap-2"
                          onSubmit={async (event) => {
                            event.preventDefault();
                            const data = new FormData(event.currentTarget);
                            const file = data.get('file');
                            const documentDate = data.get('document_date');
                            if (!file || !file.name || !documentDate) return;
                            await businessesAPI.saveFormationDocument(currentBusiness.id, document.kind, documentDate, file);
                            setDateEdits((current) => {
                              const next = { ...current };
                              delete next[editKey];
                              return next;
                            });
                            await loadBusinessData(currentBusiness.id);
                            window.dispatchEvent(new CustomEvent('businesses-updated', { detail: { businessId: currentBusiness.id } }));
                          }}
                        >
                          <label className="text-sm font-medium sm:w-40">
                            {document.label}
                            {document.hint && <span className="block text-xs font-normal text-gray-500">{document.hint}</span>}
                          </label>
                          <input
                            type="date"
                            name="document_date"
                            value={shownDate}
                            onChange={(event) => setDateEdits((current) => ({ ...current, [editKey]: event.target.value }))}
                            className="border rounded px-2 py-1 text-sm"
                            required
                          />
                          <input type="file" name="file" className="text-sm" required />
                          <button type="submit" className="bg-blue-600 text-white px-3 py-1 rounded text-sm">Save</button>
                          {formationDateIsCurrent.formationDateInWindow(storedDate, databaseToday) && (
                            <>
                              <span className="text-sm text-gray-600">Stored {storedDate}</span>
                              <button type="button" className="text-sm text-blue-700 underline" onClick={() => openFormationDocument(document.kind)}>Open</button>
                            </>
                          )}
                        </form>
                      );
                    })}
                  </div>
                )}
                {complianceCalendar.length === 0 ? (
                  <p className="text-gray-500">No compliance dates for this business.</p>
                ) : (
                  <ul className="divide-y divide-gray-200">
                    {complianceCalendar.map((item, index) => (
                      <li key={`${item.kind}-${item.title}-${index}`} className="py-3 flex flex-col sm:flex-row sm:justify-between gap-1">
                        <span className="font-medium">{item.title}</span>
                        <span className="text-sm text-gray-600 flex items-center gap-3">
                          <span>{item.date || 'No date'} · {item.status}</span>
                          {['formation_paper', 'ein_letter', 'owner_id_photo'].includes(item.kind) && (
                            <button type="button" className="text-blue-700 underline" onClick={() => openFormationDocument(item.kind)}>Open</button>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Business Details Tab */}
            {activeTab === 'details' && teamRole !== 'manager' && (
              <div className="p-4 lg:p-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-4">
                  <div>
                    <h2 className="text-lg lg:text-2xl font-bold">{currentBusiness.name || currentBusiness.legal_name}</h2>
                    {parentBusiness ? (
                      <p className="text-sm text-indigo-700 mt-1">
                        Child business under{' '}
                        <button
                          type="button"
                          onClick={() => updateSelectedBusiness(parentBusiness.id)}
                          className="underline font-medium hover:text-indigo-900"
                        >
                          {parentBusiness.name || parentBusiness.legal_name}
                        </button>
                      </p>
                    ) : (
                      <p className="text-sm text-gray-500 mt-1">Parent business entity</p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {!currentBusiness.parent_business_id && (
                      <button
                        onClick={() => openCreateChildModal(currentBusiness.id)}
                        className="bg-indigo-600 text-white px-3 lg:px-4 py-2 rounded hover:bg-indigo-700 text-xs lg:text-sm"
                      >
                        + Add Child Business
                      </button>
                    )}
                    <button
                      onClick={() => handleEditBusiness(currentBusiness)}
                      className="bg-blue-600 text-white px-3 lg:px-4 py-2 rounded hover:bg-blue-700 text-xs lg:text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteBusiness(currentBusiness.id)}
                      className="bg-red-600 text-white px-3 lg:px-4 py-2 rounded hover:bg-red-700 text-xs lg:text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                <div className="mb-6">
                  <BusinessLogoManager
                    businessId={currentBusiness.id}
                    onChange={async () => {
                      await loadBusinesses();
                    }}
                  />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                  <div>
                    <h3 className="font-semibold text-base lg:text-lg mb-3">General Information</h3><DatingMethodSetting businessId={currentBusiness.id} /><ClientPortalSettings businessId={currentBusiness.id} />
                    <dl className="space-y-2 text-sm lg:text-base">
                      <div><dt className="font-medium text-gray-600">Hierarchy:</dt><dd>{parentBusiness ? `Child of ${parentBusiness.name || parentBusiness.legal_name}` : 'Parent / top-level'}</dd></div>
                      <div><dt className="font-medium text-gray-600">Legal Name:</dt><dd>{currentBusiness.legal_name || 'N/A'}</dd></div>
                      <div><dt className="font-medium text-gray-600">DBA Name:</dt><dd>{currentBusiness.dba_name || 'N/A'}</dd></div>
                      <div><dt className="font-medium text-gray-600">Legal Structure:</dt><dd>{currentBusiness.legal_structure || 'N/A'}</dd></div>
                      {currentBusiness.legal_structure !== 'Personal' && filingScreensOpen && (
                      <div><dt className="font-medium text-gray-600">Tax ID/EIN:</dt><dd>{currentBusiness.tax_id || 'N/A'}</dd></div>
                      )}
                      <div><dt className="font-medium text-gray-600">Industry:</dt><dd>{currentBusiness.industry || 'N/A'}</dd></div>
                      <div><dt className="font-medium text-gray-600">Established:</dt><dd>{formatDate(currentBusiness.established_date)}</dd></div>
                      <div><dt className="font-medium text-gray-600">Fiscal Year End:</dt><dd>{currentBusiness.fiscal_year_end_month}/{currentBusiness.fiscal_year_end_day}</dd></div>
                      <div><dt className="font-medium text-gray-600">Status:</dt><dd>{getStatusBadge(currentBusiness.is_active ? 'active' : 'inactive')}</dd></div>
                    </dl>
                  </div>

                  <div>
                    <h3 className="font-semibold text-base lg:text-lg mb-3">Contact Information</h3>
                    <dl className="space-y-2 text-sm lg:text-base">
                      <div><dt className="font-medium text-gray-600">Address:</dt><dd>{currentBusiness.address_line1 || 'N/A'}</dd></div>
                      {currentBusiness.address_line2 && <div><dd>{currentBusiness.address_line2}</dd></div>}
                      <div><dd>{currentBusiness.city}, {currentBusiness.state} {currentBusiness.zip_code}</dd></div>
                      <div><dd>{currentBusiness.country || 'USA'}</dd></div>
                      <div><dt className="font-medium text-gray-600 mt-3">Phone:</dt><dd>{currentBusiness.phone || 'N/A'}</dd></div>
                      <div><dt className="font-medium text-gray-600">Email:</dt><dd>{currentBusiness.email || 'N/A'}</dd></div>
                      <div><dt className="font-medium text-gray-600">Website:</dt><dd>{currentBusiness.website || 'N/A'}</dd></div>
                    </dl>
                  </div>
                </div>

                {currentBusiness.description && (
                  <div className="mt-6">
                    <h3 className="font-semibold text-lg mb-2">Description</h3>
                    <p className="text-gray-700">{currentBusiness.description}</p>
                  </div>
                )}

                {currentBusiness.notes && (
                  <div className="mt-6">
                    <h3 className="font-semibold text-lg mb-2">Notes</h3>
                    <p className="text-gray-700 whitespace-pre-wrap">{currentBusiness.notes}</p>
                  </div>
                )}

                {/* Child businesses under this parent umbrella */}
                {!currentBusiness.parent_business_id && (
                  <div className="mt-6 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                      <h3 className="font-semibold text-lg">Child Businesses Under This Umbrella</h3>
                      <button
                        type="button"
                        onClick={() => openCreateChildModal(currentBusiness.id)}
                        className="bg-indigo-600 text-white px-3 py-1.5 rounded text-sm hover:bg-indigo-700"
                      >
                        + Add Child Business
                      </button>
                    </div>
                    {childBusinesses.length === 0 ? (
                      <p className="text-sm text-gray-600">
                        {currentBusiness.legal_structure === 'Personal'
                          ? 'No child locations yet.'
                          : 'No child businesses yet. Add a child when you have a separate legal entity (own EIN) that should still report under this parent.'}
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {childBusinesses.map((child) => (
                          <button
                            key={child.id}
                            type="button"
                            onClick={() => updateSelectedBusiness(child.id)}
                            className="bg-white p-3 rounded border border-indigo-300 text-left hover:border-indigo-500 hover:shadow-sm"
                          >
                            <div className="font-semibold text-indigo-700">{child.name || child.legal_name}</div>
                            <div className="text-sm text-gray-600">{child.legal_structure || 'Business'}</div>
                            {currentBusiness.legal_structure !== 'Personal' && filingScreensOpen && child.tax_id && <div className="text-xs text-gray-500 mt-1">EIN: {child.tax_id}</div>}
                            {child.industry && <div className="text-xs text-gray-500">Industry: {child.industry}</div>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* State-registration divisions (same EIN) — distinct from child businesses */}
                {currentBusiness.legal_structure !== 'Personal' && filingScreensOpen && stateRegistrations.filter(r => r.is_subsidiary).length > 0 && (
                  <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="font-semibold text-lg mb-3">Divisions & DBAs (Same EIN)</h3>
                    <p className="text-xs text-gray-600 mb-3">
                      These are state registrations / trade names under this legal entity — not separate businesses.
                      They do not appear in the left sidebar. Use <span className="font-medium">Create as child business</span>
                      {' '}if you need a selectable entity with its own tracking.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {stateRegistrations
                        .filter(r => r.is_subsidiary)
                        .map(sub => (
                          <div key={sub.id} className="bg-white p-3 rounded border border-blue-300">
                            <div className="font-semibold text-blue-700">{sub.subsidiary_name}</div>
                            <div className="text-sm text-gray-600">{sub.state} • {sub.registration_type}</div>
                            {sub.industry && <div className="text-xs text-gray-500 mt-1">Industry: {sub.industry}</div>}
                            {sub.dba_name && <div className="text-xs text-gray-500">DBA: {sub.dba_name}</div>}
                            <DivisionBillingSettings businessId={currentBusiness.id} registration={sub} />
                            <button
                              type="button"
                              onClick={() => openCreateChildModal(currentBusiness.id, {
                                name: sub.subsidiary_name || sub.dba_name || '',
                                industry: sub.industry || '',
                                description: sub.subsidiary_name
                                  ? `Child business created from division/DBA: ${sub.subsidiary_name}`
                                  : ''
                              })}
                              className="mt-3 text-xs font-medium text-indigo-700 hover:text-indigo-900 underline"
                            >
                              Create as child business
                            </button>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* State Registrations Tab */}
            {activeTab === 'registrations' && currentBusiness.legal_structure !== 'Personal' && filingScreensOpen && (
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold">State Registrations & DBAs</h2>
                  <button
                    onClick={() => {
                      resetRegistrationForm();
                      setEditingItem(null);
                      setShowRegistrationModal(true);
                    }}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                  >
                    + Add Registration
                  </button>
                </div>

                {stateRegistrations.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No state registrations. Add a registration to track where your business operates.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">State</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">DBA Name</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subsidiary</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Registration #</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reg. Date</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Filing Fee</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {stateRegistrations.map((reg) => (
                          <tr key={reg.id} className={reg.is_subsidiary ? 'bg-blue-50' : ''}>
                            <td className="px-4 py-3">{reg.state}</td>
                            <td className="px-4 py-3">{reg.registration_type}</td>
                            <td className="px-4 py-3">{reg.dba_name || '—'}</td>
                            <td className="px-4 py-3">
                              {reg.is_subsidiary ? (
                                <div>
                                  <div className="font-medium text-blue-700">{reg.subsidiary_name}</div>
                                  {reg.industry && <div className="text-xs text-gray-500">{reg.industry}</div>}
                                </div>
                              ) : '—'}
                            </td>
                            <td className="px-4 py-3">{reg.registration_number || '—'}</td>
                            <td className="px-4 py-3">{formatDate(reg.registration_date)}</td>
                            <td className="px-4 py-3">{getStatusBadge(reg.status)}</td>
                            <td className="px-4 py-3">{formatCurrency(reg.filing_fee)}</td>
                            <td className="px-4 py-3 space-x-2">
                              {reg.is_subsidiary && (
                                <button
                                  type="button"
                                  onClick={() => openCreateChildModal(selectedBusiness, {
                                    name: reg.subsidiary_name || reg.dba_name || '',
                                    industry: reg.industry || '',
                                    description: reg.subsidiary_name
                                      ? `Child business created from division/DBA: ${reg.subsidiary_name}`
                                      : ''
                                  })}
                                  className="text-indigo-600 hover:text-indigo-800"
                                >
                                  As child
                                </button>
                              )}
                              <button
                                onClick={() => handleEditRegistration(reg)}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteRegistration(reg.id)}
                                className="text-red-600 hover:text-red-800"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Registered Agents Tab */}
            {activeTab === 'agents' && currentBusiness.legal_structure !== 'Personal' && filingScreensOpen && (
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold">Registered Agents</h2>
                  <button
                    onClick={() => {
                      resetAgentForm();
                      setEditingItem(null);
                      setShowAgentModal(true);
                    }}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                  >
                    + Add Agent
                  </button>
                </div>

                {registeredAgents.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No registered agents. Add an agent to track your compliance requirements.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">State</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agent Name</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hire Date</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Renewal Freq.</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Next Renewal</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cost</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {registeredAgents.map((agent) => (
                          <tr key={agent.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium">{agent.state}</td>
                            <td className="px-4 py-3">{agent.agent_name}</td>
                            <td className="px-4 py-3">{agent.agent_company || '-'}</td>
                            <td className="px-4 py-3">
                              <div className="text-sm">{agent.agent_phone || '-'}</div>
                              <div className="text-xs text-gray-500">{agent.agent_email || '-'}</div>
                            </td>
                            <td className="px-4 py-3">{formatDate(agent.hire_date)}</td>
                            <td className="px-4 py-3 capitalize">{agent.renewal_frequency}</td>
                            <td className="px-4 py-3">
                              <div>{formatDate(agent.next_renewal_date)}</div>
                              <div className="text-sm">{getRenewalAlert(agent.next_renewal_date)}</div>
                            </td>
                            <td className="px-4 py-3">{formatCurrency(agent.cost_per_renewal)}</td>
                            <td className="px-4 py-3">{getStatusBadge(agent.status)}</td>
                            <td className="px-4 py-3">
                              <button
                                onClick={() => handleEditAgent(agent)}
                                className="text-blue-600 hover:text-blue-800 mr-3"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteAgent(agent.id)}
                                className="text-red-600 hover:text-red-800"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Renewal Alerts */}
                {registeredAgents.some(a => {
                  const daysUntil = Math.ceil((parseCalendarDay(a.next_renewal_date) - new Date()) / (1000 * 60 * 60 * 24));
                  return daysUntil <= 90;
                }) && (
                  <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h3 className="font-semibold text-yellow-800 mb-2">⚠️ Upcoming Renewals (Next 90 Days)</h3>
                    <ul className="space-y-1">
                      {registeredAgents
                        .filter(a => {
                          const daysUntil = Math.ceil((parseCalendarDay(a.next_renewal_date) - new Date()) / (1000 * 60 * 60 * 24));
                          return daysUntil <= 90;
                        })
                        .map(agent => (
                          <li key={agent.id} className="text-sm">
                            <strong>{agent.state}</strong> - {agent.agent_name}: {getRenewalAlert(agent.next_renewal_date)}
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Team Access Tab — division-scoped invites */}
            {activeTab === 'team' && canSeeTeam && (
              <div className="p-4 lg:p-6 space-y-8">
                <div>
                  <h2 className="text-xl font-bold mb-1">Team Access</h2>
                  <p className="text-sm text-gray-600">
                    Invite employees to this business. Select one or more divisions/DBAs to restrict them to that
                    operations data only (Transactions &amp; Budgets). Leave divisions unchecked for full business access.
                  </p>
                </div>

                {canInviteTeam && (
                <form onSubmit={handleInviteSubmit} className="bg-slate-50 border rounded-lg p-4 space-y-4">
                  <h3 className="font-semibold">Invite teammate</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Email *</label>
                      <input
                        type="email"
                        required
                        value={inviteForm.email}
                        onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                        className="w-full p-2 border rounded"
                        placeholder="employee@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Role</label>
                      <select
                        value={inviteForm.member_role}
                        onChange={(e) => setInviteForm({ ...inviteForm, member_role: e.target.value })}
                        className="w-full p-2 border rounded"
                      >
                        {INVITE_ROLES.map((r) => (
                          <option key={r.value} value={r.value}>{r.label} — {r.sees}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Division scope (optional)</label>
                    {stateRegistrations.filter((r) => r.is_subsidiary).length === 0 ? (
                      <p className="text-xs text-gray-500">
                        No divisions/DBAs yet. Add a State Registration marked as a division first, then you can
                        scope invites to it.
                      </p>
                    ) : (
                      <div className="flex flex-wrap gap-3">
                        {stateRegistrations.filter((r) => r.is_subsidiary).map((reg) => (
                          <label key={reg.id} className="inline-flex items-center gap-2 text-sm bg-white border rounded px-3 py-2">
                            <input
                              type="checkbox"
                              checked={inviteForm.state_registration_ids.includes(Number(reg.id))}
                              onChange={() => toggleInviteDivision(reg.id)}
                            />
                            {reg.subsidiary_name || reg.dba_name} ({reg.state})
                          </label>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-2">
                      Checked = division-only access. Unchecked all = full corporate access for that member.
                    </p>
                  </div>
                  <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700">
                    Send invite
                  </button>
                </form>
                )}

                <div>
                  <h3 className="font-semibold mb-3">Pending invites</h3>
                  {teamLoading ? (
                    <p className="text-sm text-gray-500">Loading…</p>
                  ) : teamInvites.length === 0 ? (
                    <p className="text-sm text-gray-500">No pending invites.</p>
                  ) : (
                    <ul className="divide-y border rounded">
                      {teamInvites.map((inv) => (
                        <li key={inv.id} className="p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm">
                          <div>
                            <div className="font-medium">{inv.invitee_email}</div>
                            <div className="text-gray-500">
                              Role: {inv.member_role}
                              {Array.isArray(inv.state_registration_ids) && inv.state_registration_ids.length > 0
                                ? ` · Divisions: ${inv.state_registration_ids.length}`
                                : ' · Full business'}
                            </div>
                          </div>
                          {canInviteTeam && (
                          <button
                            type="button"
                            onClick={() => handleRevokeInvite(inv.invite_token)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            Revoke
                          </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div>
                  <h3 className="font-semibold mb-3">Members</h3>
                  {teamLoading ? (
                    <p className="text-sm text-gray-500">Loading…</p>
                  ) : teamMembers.length === 0 ? (
                    <p className="text-sm text-gray-500">No members found.</p>
                  ) : (
                    <div className="space-y-3">
                      {teamMembers.map((m) => {
                        const divisions = stateRegistrations.filter((r) => r.is_subsidiary);
                        const scopedIds = (m.state_registration_ids || []).map(Number);
                        return (
                          <div key={m.user_id} className="border rounded p-3">
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                              <div>
                                <div className="font-medium">
                                  {m.first_name || m.last_name
                                    ? `${m.first_name || ''} ${m.last_name || ''}`.trim()
                                    : m.email}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {m.email} · {m.member_role}
                                  {m.access_mode === 'division' ? ' · Division-scoped' : ' · Full access'}
                                </div>
                              </div>
                            </div>
                            {canManagePeople && !m.is_owner && (
                              <div className="flex flex-wrap items-center gap-2">
                                <select
                                  value={m.member_role}
                                  onChange={(e) => adminApi.addMember(currentBusiness.id, { user_id: m.user_id, member_role: e.target.value }).then(() => loadTeamAccess(selectedBusiness))}
                                  className="border rounded px-2 py-1 text-sm"
                                >
                                  {INVITE_ROLES.map((r) => (
                                    <option key={r.value} value={r.value}>{r.label}</option>
                                  ))}
                                </select>
                                <button type="button"
                                  onClick={() => adminApi.removeMember(currentBusiness.id, m.user_id).then(() => loadTeamAccess(selectedBusiness))}
                                  className="text-red-600 text-sm">
                                  Remove
                                </button>
                                <BankFeedsAccess businessId={currentBusiness.id} member={m} onSaved={() => loadTeamAccess(selectedBusiness)} />
                              </div>
                            )}
                            {canManagePeople && !m.is_owner && divisions.length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-2">
                                {divisions.map((reg) => (
                                  <label key={reg.id} className="inline-flex items-center gap-1.5 text-xs bg-gray-50 border rounded px-2 py-1">
                                    <input
                                      type="checkbox"
                                      checked={scopedIds.includes(Number(reg.id))}
                                      onChange={() => handleUpdateMemberDivisions(m.user_id, scopedIds, reg.id)}
                                    />
                                    {reg.subsidiary_name || reg.dba_name}
                                  </label>
                                ))}
                              </div>
                            )}
                            {m.is_owner && (
                              <p className="text-xs text-gray-500">Owner always has full business access.</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      ) : null}

      {/* Business Modal */}
      {showBusinessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white">
              <h2 className="text-2xl font-bold">
                {editingItem
                  ? 'Edit Business Entity'
                  : isChildForm
                    ? 'Create Child Business'
                    : 'Create Parent Business Entity'}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {isChildForm
                  ? 'Creates a full separate business (own legal entity / EIN) that falls under the selected parent umbrella.'
                  : 'Creates your main business entity (corporation, LLC, etc). After creating, you can add child businesses under it, plus state registrations and DBAs.'}
              </p>
            </div>
            <form onSubmit={handleBusinessSubmit} className="p-6">
              {/* Hierarchy */}
              <div className={`mb-6 border rounded-lg p-4 ${isChildForm ? 'bg-indigo-50 border-indigo-200' : 'bg-slate-50 border-slate-200'}`}>
                <h3 className="font-semibold mb-2">
                  {isChildForm ? 'Parent Umbrella' : 'Business Hierarchy'}
                </h3>
                <label className="block text-sm font-medium mb-1">
                  Parent Business {isChildForm ? '*' : '(optional)'}
                </label>
                <select
                  required={isChildForm && !editingItem}
                  value={businessForm.parent_business_id || ''}
                  onChange={(e) => setBusinessForm({ ...businessForm, parent_business_id: e.target.value })}
                  className="w-full p-2 border rounded bg-white"
                >
                  <option value="">None — this is a top-level / parent business</option>
                  {parentCandidates.map((business) => (
                    <option key={business.id} value={business.id}>
                      {getBusinessOptionLabel(business)}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-600 mt-2">
                  {isChildForm
                    ? 'The child is still a real business with its own accounts and records; it is linked to the parent for rollup and organization.'
                    : 'Leave blank for a parent. To nest under an existing business, choose that parent here.'}
                </p>
              </div>

              {!editingItem && (
                <div className="mb-4 grid grid-cols-2 gap-3" role="radiogroup" aria-label="Location type">
                  {[['Personal', 'Personal'], ['LLC', 'Business']].map(([structure, label]) => {
                    const chosen = structure === 'Personal'
                      ? businessForm.legal_structure === 'Personal'
                      : businessForm.legal_structure !== 'Personal';
                    return (
                      <button
                        key={label}
                        type="button"
                        role="radio"
                        aria-checked={chosen}
                        data-location-kind={label.toLowerCase()}
                        onClick={() => setBusinessForm({
                          ...businessForm,
                          legal_structure: structure,
                          ...(structure === 'Personal' ? { legal_name: '', tax_id: '' } : {})
                        })}
                        className={`border rounded p-2 font-medium ${chosen ? 'border-blue-600 bg-blue-50' : 'border-gray-300'}`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Essential Business Info Section */}
              <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-3">Core Business Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Business Name *</label>
                    <input
                      type="text"
                      required
                      value={businessForm.name}
                      onChange={(e) => setBusinessForm({ ...businessForm, name: e.target.value })}
                      className="w-full p-2 border rounded"
                      placeholder={isChildForm ? 'e.g., TransportCo LLC' : 'e.g., Elova Enterprises Inc'}
                    />
                  </div>
                  {businessForm.legal_structure !== 'Personal' && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Legal Name</label>
                    <input
                      type="text"
                      value={businessForm.legal_name}
                      onChange={(e) => setBusinessForm({ ...businessForm, legal_name: e.target.value })}
                      className="w-full p-2 border rounded"
                      placeholder="Full legal name if different"
                    />
                  </div>
                  )}
                  {(editingItem || businessForm.legal_structure !== 'Personal') && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Legal Structure *</label>
                    <select
                      required
                      value={businessForm.legal_structure}
                      onChange={(e) => setBusinessForm({ ...businessForm, legal_structure: e.target.value })}
                      className="w-full p-2 border rounded"
                    >
                      {LEGAL_STRUCTURES.map(structure => (
                        <option key={structure} value={structure}>{structure}</option>
                      ))}
                    </select>
                  </div>
                  )}
                  {businessForm.legal_structure !== 'Personal' && !creatingFormedLocation && (!editingItem || editFilingOpen) && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Tax ID/EIN *</label>
                    <input
                      type="text"
                      required
                      value={businessForm.tax_id}
                      onChange={(e) => setBusinessForm({ ...businessForm, tax_id: e.target.value })}
                      className="w-full p-2 border rounded"
                      placeholder="XX-XXXXXXX"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {isChildForm
                        ? 'Use this child entity\'s own EIN (separate from the parent).'
                        : 'This EIN will be used for this entity\'s state registrations.'}
                    </p>
                  </div>
                  )}
                </div>
              </div>

              {/* Optional Details Section */}
              <div className="mb-4">
                <h3 className="font-semibold text-gray-700 mb-3">📋 Additional Details (Optional)</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Industry</label>
                    <input
                      type="text"
                      value={businessForm.industry}
                      onChange={(e) => setBusinessForm({ ...businessForm, industry: e.target.value })}
                      className="w-full p-2 border rounded"
                      placeholder="e.g., Technology, Consulting"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Established Date</label>
                    <input
                      type="date"
                      value={businessForm.established_date}
                      onChange={(e) => setBusinessForm({ ...businessForm, established_date: e.target.value })}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">Description</label>
                    <textarea
                      value={businessForm.description}
                      onChange={(e) => setBusinessForm({ ...businessForm, description: e.target.value })}
                      className="w-full p-2 border rounded"
                      rows="2"
                      placeholder="Brief description of your business"
                    />
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="font-semibold text-gray-700 mb-3">🎨 Business Logo</h3>
                <div className="flex items-center gap-4">
                  <div className="h-16 w-16 rounded-md border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden">
                    {businessLogoPreview ? (
                      <img src={businessLogoPreview} alt="Business Logo Preview" className="object-contain h-full w-full" onError={() => setBusinessLogoPreview(null)} />
                    ) : (
                      <span className="text-xs text-gray-500 text-center px-1">No Logo</span>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => businessLogoInputRef.current?.click()}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                      >
                        {businessLogoPreview ? 'Replace Logo' : 'Add Logo'}
                      </button>
                      {businessLogoPreview && (
                        <button
                          type="button"
                          onClick={clearBusinessLogoSelection}
                          className="px-3 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">
                      Upload a square PNG/JPG/WebP under {MAX_LOGO_SIZE_MB}MB. The logo appears in Business Management, Settings, and invoice PDFs.
                    </p>
                    <input
                      ref={businessLogoInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      onChange={handleBusinessLogoSelect}
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information Section */}
              <div className="mb-4">
                <h3 className="font-semibold text-gray-700 mb-3">📍 Contact Information (Optional)</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">Address Line 1</label>
                    <input
                      type="text"
                      value={businessForm.address_line1}
                      onChange={(e) => setBusinessForm({ ...businessForm, address_line1: e.target.value })}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">Address Line 2</label>
                    <input
                      type="text"
                      value={businessForm.address_line2}
                      onChange={(e) => setBusinessForm({ ...businessForm, address_line2: e.target.value })}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">City</label>
                    <input
                      type="text"
                      value={businessForm.city}
                      onChange={(e) => setBusinessForm({ ...businessForm, city: e.target.value })}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">State</label>
                    <select
                      value={businessForm.state}
                      onChange={(e) => setBusinessForm({ ...businessForm, state: e.target.value })}
                      className="w-full p-2 border rounded"
                    >
                      <option value="">Select State</option>
                      {US_STATES.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">ZIP Code</label>
                    <input
                      type="text"
                      value={businessForm.zip_code}
                      onChange={(e) => setBusinessForm({ ...businessForm, zip_code: e.target.value })}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Country</label>
                    <input
                      type="text"
                      value={businessForm.country}
                      onChange={(e) => setBusinessForm({ ...businessForm, country: e.target.value })}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Phone</label>
                    <input
                      type="tel"
                      value={businessForm.phone}
                      onChange={(e) => setBusinessForm({ ...businessForm, phone: e.target.value })}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Email</label>
                    <input
                      type="email"
                      value={businessForm.email}
                      onChange={(e) => setBusinessForm({ ...businessForm, email: e.target.value })}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Website</label>
                    <input
                      type="url"
                      value={businessForm.website}
                      onChange={(e) => setBusinessForm({ ...businessForm, website: e.target.value })}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Fiscal Year End Month</label>
                    <input
                      type="number"
                      min="1"
                      max="12"
                      value={businessForm.fiscal_year_end_month}
                      onChange={(e) => setBusinessForm({ ...businessForm, fiscal_year_end_month: parseInt(e.target.value) })}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Fiscal Year End Day</label>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={businessForm.fiscal_year_end_day}
                      onChange={(e) => setBusinessForm({ ...businessForm, fiscal_year_end_day: parseInt(e.target.value) })}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">Notes</label>
                    <textarea
                      value={businessForm.notes}
                      onChange={(e) => setBusinessForm({ ...businessForm, notes: e.target.value })}
                      className="w-full p-2 border rounded"
                      rows="3"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowBusinessModal(false);
                    setEditingItem(null);
                    resetBusinessForm();
                  }}
                  className="px-4 py-2 border rounded hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
                >
                  {editingItem
                    ? 'Update Business Entity'
                    : isChildForm
                      ? 'Create Child Business'
                      : 'Create Parent Business'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Registration Modal */}
      {showRegistrationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white">
              <h2 className="text-2xl font-bold">
                {editingItem ? 'Edit State Registration' : 'Add State Registration'}
              </h2>
            </div>
            <form onSubmit={handleRegistrationSubmit} className="p-6">
              {/* Info Box */}
              {currentBusiness && (
                <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <span className="text-2xl mr-3">ℹ️</span>
                    <div className="flex-1">
                      <h3 className="font-semibold text-blue-900 mb-1">Adding State Operation for: {currentBusiness.name}</h3>
                      <p className="text-sm text-blue-700">
                        Business info (EIN: {currentBusiness.tax_id || 'N/A'}) is automatically linked. 
                        Just provide state-specific details below.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">State *</label>
                  <select
                    required
                    value={registrationForm.state}
                    onChange={(e) => setRegistrationForm({ ...registrationForm, state: e.target.value })}
                    className="w-full p-2 border rounded"
                  >
                    <option value="">Select State</option>
                    {US_STATES.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Registration Type *</label>
                  <select
                    required
                    value={registrationForm.registration_type}
                    onChange={(e) => setRegistrationForm({ ...registrationForm, registration_type: e.target.value })}
                    className="w-full p-2 border rounded"
                  >
                    {REGISTRATION_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">DBA Name</label>
                  <input
                    type="text"
                    value={registrationForm.dba_name}
                    onChange={(e) => setRegistrationForm({ ...registrationForm, dba_name: e.target.value })}
                    className="w-full p-2 border rounded"
                    placeholder="Leave blank if same as business name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Registration Number</label>
                  <input
                    type="text"
                    value={registrationForm.registration_number}
                    onChange={(e) => setRegistrationForm({ ...registrationForm, registration_number: e.target.value })}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Registration Date</label>
                  <input
                    type="date"
                    value={registrationForm.registration_date}
                    onChange={(e) => setRegistrationForm({ ...registrationForm, registration_date: e.target.value })}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Status</label>
                  <select
                    value={registrationForm.status}
                    onChange={(e) => setRegistrationForm({ ...registrationForm, status: e.target.value })}
                    className="w-full p-2 border rounded"
                  >
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="expired">Expired</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Expiration Date</label>
                  <input
                    type="date"
                    value={registrationForm.expiration_date}
                    onChange={(e) => setRegistrationForm({ ...registrationForm, expiration_date: e.target.value })}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Annual Report Due Date</label>
                  <input
                    type="date"
                    value={registrationForm.annual_report_due_date}
                    onChange={(e) => setRegistrationForm({ ...registrationForm, annual_report_due_date: e.target.value })}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Filing Fee</label>
                  <input
                    type="number"
                    step="0.01"
                    value={registrationForm.filing_fee}
                    onChange={(e) => setRegistrationForm({ ...registrationForm, filing_fee: e.target.value })}
                    className="w-full p-2 border rounded"
                  />
                </div>
                
                {/* Same-EIN division/DBA tracking (not a child business) */}
                <div className="col-span-2 border-t pt-4 mt-4">
                  <div className="flex items-start mb-2">
                    <input
                      type="checkbox"
                      id="is_subsidiary"
                      checked={registrationForm.is_subsidiary}
                      onChange={(e) => setRegistrationForm({ 
                        ...registrationForm, 
                        is_subsidiary: e.target.checked,
                        subsidiary_name: e.target.checked ? registrationForm.subsidiary_name : '',
                        industry: e.target.checked ? registrationForm.industry : ''
                      })}
                      className="h-4 w-4 text-blue-600 rounded mr-2 mt-0.5"
                    />
                    <div>
                      <label htmlFor="is_subsidiary" className="text-sm font-medium">
                        This is a separate division/DBA under the same EIN
                      </label>
                      <p className="text-xs text-gray-500 mt-1">
                        For a separate legal entity with its own EIN, use Add Child Business instead.
                      </p>
                    </div>
                  </div>
                  
                  {registrationForm.is_subsidiary && (
                    <div className="grid grid-cols-2 gap-4 pl-6 border-l-2 border-blue-200 mt-3">
                      <div>
                        <label className="block text-sm font-medium mb-1">Division/DBA Name *</label>
                        <input
                          type="text"
                          required={registrationForm.is_subsidiary}
                          value={registrationForm.subsidiary_name}
                          onChange={(e) => setRegistrationForm({ ...registrationForm, subsidiary_name: e.target.value })}
                          className="w-full p-2 border rounded"
                          placeholder="e.g., Elova Transport Solutions"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Industry/Focus</label>
                        <input
                          type="text"
                          value={registrationForm.industry}
                          onChange={(e) => setRegistrationForm({ ...registrationForm, industry: e.target.value })}
                          className="w-full p-2 border rounded"
                          placeholder="e.g., Trucking, Robotics"
                        />
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Notes</label>
                  <textarea
                    value={registrationForm.notes}
                    onChange={(e) => setRegistrationForm({ ...registrationForm, notes: e.target.value })}
                    className="w-full p-2 border rounded"
                    rows="3"
                  />
                </div>
              </div>
              <div className="flex justify-between items-center mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowRegistrationModal(false);
                    resetAgentForm();
                    setAgentForm({ ...agentForm, state: registrationForm.state });
                    setShowAgentModal(true);
                  }}
                  className="px-4 py-2 border border-green-600 text-green-600 rounded hover:bg-green-50 flex items-center gap-2"
                >
                  <span>👤</span> Add Registered Agent for {registrationForm.state || 'this State'}
                </button>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowRegistrationModal(false);
                      setEditingItem(null);
                      resetRegistrationForm();
                    }}
                    className="px-4 py-2 border rounded hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    {editingItem ? 'Update' : 'Add'} Registration
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Agent Modal */}
      {showAgentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white">
              <h2 className="text-2xl font-bold">
                {editingItem ? 'Edit Registered Agent' : 'Add Registered Agent'}
              </h2>
            </div>
            <form onSubmit={handleAgentSubmit} className="p-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">State *</label>
                  <select
                    required
                    value={agentForm.state}
                    onChange={(e) => setAgentForm({ ...agentForm, state: e.target.value })}
                    className="w-full p-2 border rounded"
                  >
                    <option value="">Select State</option>
                    {US_STATES.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Agent Type</label>
                  <select
                    value={agentForm.agent_type}
                    onChange={(e) => setAgentForm({ ...agentForm, agent_type: e.target.value })}
                    className="w-full p-2 border rounded"
                  >
                    <option value="registered_agent">Registered Agent</option>
                    <option value="statutory_agent">Statutory Agent</option>
                    <option value="resident_agent">Resident Agent</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Agent Name *</label>
                  <input
                    type="text"
                    required
                    value={agentForm.agent_name}
                    onChange={(e) => setAgentForm({ ...agentForm, agent_name: e.target.value })}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Agent Company</label>
                  <input
                    type="text"
                    value={agentForm.agent_company}
                    onChange={(e) => setAgentForm({ ...agentForm, agent_company: e.target.value })}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Address Line 1</label>
                  <input
                    type="text"
                    value={agentForm.agent_address_line1}
                    onChange={(e) => setAgentForm({ ...agentForm, agent_address_line1: e.target.value })}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Address Line 2</label>
                  <input
                    type="text"
                    value={agentForm.agent_address_line2}
                    onChange={(e) => setAgentForm({ ...agentForm, agent_address_line2: e.target.value })}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">City</label>
                  <input
                    type="text"
                    value={agentForm.agent_city}
                    onChange={(e) => setAgentForm({ ...agentForm, agent_city: e.target.value })}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">State</label>
                  <select
                    value={agentForm.agent_state}
                    onChange={(e) => setAgentForm({ ...agentForm, agent_state: e.target.value })}
                    className="w-full p-2 border rounded"
                  >
                    <option value="">Select State</option>
                    {US_STATES.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">ZIP Code</label>
                  <input
                    type="text"
                    value={agentForm.agent_zip}
                    onChange={(e) => setAgentForm({ ...agentForm, agent_zip: e.target.value })}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Phone</label>
                  <input
                    type="tel"
                    value={agentForm.agent_phone}
                    onChange={(e) => setAgentForm({ ...agentForm, agent_phone: e.target.value })}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <input
                    type="email"
                    value={agentForm.agent_email}
                    onChange={(e) => setAgentForm({ ...agentForm, agent_email: e.target.value })}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Hire Date *</label>
                  <input
                    type="date"
                    required
                    value={agentForm.hire_date}
                    onChange={(e) => setAgentForm({ ...agentForm, hire_date: e.target.value })}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Renewal Frequency *</label>
                  <select
                    required
                    value={agentForm.renewal_frequency}
                    onChange={(e) => setAgentForm({ ...agentForm, renewal_frequency: e.target.value })}
                    className="w-full p-2 border rounded"
                  >
                    {RENEWAL_FREQUENCIES.map(freq => (
                      <option key={freq.value} value={freq.value}>{freq.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Frequency Value</label>
                  <input
                    type="number"
                    min="1"
                    value={agentForm.renewal_frequency_value}
                    onChange={(e) => setAgentForm({ ...agentForm, renewal_frequency_value: parseInt(e.target.value) })}
                    className="w-full p-2 border rounded"
                  />
                  <p className="text-xs text-gray-500 mt-1">Number of periods (e.g., 2 for every 2 years)</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Last Renewal Date</label>
                  <input
                    type="date"
                    value={agentForm.last_renewal_date}
                    onChange={(e) => setAgentForm({ ...agentForm, last_renewal_date: e.target.value })}
                    className="w-full p-2 border rounded"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave blank if never renewed</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Cost per Renewal</label>
                  <input
                    type="number"
                    step="0.01"
                    value={agentForm.cost_per_renewal}
                    onChange={(e) => setAgentForm({ ...agentForm, cost_per_renewal: e.target.value })}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Status</label>
                  <select
                    value={agentForm.status}
                    onChange={(e) => setAgentForm({ ...agentForm, status: e.target.value })}
                    className="w-full p-2 border rounded"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={agentForm.auto_renew}
                      onChange={(e) => setAgentForm({ ...agentForm, auto_renew: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium">Auto-Renew</span>
                  </label>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Notes</label>
                  <textarea
                    value={agentForm.notes}
                    onChange={(e) => setAgentForm({ ...agentForm, notes: e.target.value })}
                    className="w-full p-2 border rounded"
                    rows="3"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAgentModal(false);
                    setEditingItem(null);
                    resetAgentForm();
                  }}
                  className="px-4 py-2 border rounded hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  {editingItem ? 'Update' : 'Add'} Agent
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    </ErrorBoundary>
  );
}

export default Business;
